import Box from "@/elements/Box";
import Typography from "@/elements/Typography";
import { Card, Tooltip, Link } from "@mui/material";
import {
  NexusAttuatoreDto,
  ProprietaMisurataDto,
  useGetModelliDispositivo,
} from "@/api";
import WarningIcon from "@mui/icons-material/Warning";
import NotificationsIcon from "@mui/icons-material/Notifications";
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import "./style.css";

interface NexusAttuatoreCardProps {
  attuatore: NexusAttuatoreDto;
  proprietaNexusPinMisurate: ProprietaMisurataDto[];
}

const NexusAttuatoreCard = ({
  attuatore,
  proprietaNexusPinMisurate,
}: NexusAttuatoreCardProps) => {
  const { t } = useTranslation();
  const { data: modelliResponse } = useGetModelliDispositivo();
  const modelli = modelliResponse?.data;

  const riduzionePinProp = useMemo(() => {
    if (attuatore.riduzionePin) {
      return (
        proprietaNexusPinMisurate.find(
          (p) => p.type.influxDbTag === attuatore.riduzionePin
        ) ?? null
      );
    } else {
      return null;
    }
  }, [attuatore.riduzionePin, proprietaNexusPinMisurate]);

  const statoAnalisiPinProp = useMemo(() => {
    if (attuatore.statoAnalisiPin) {
      return (
        proprietaNexusPinMisurate.find(
          (p) => p.type.influxDbTag === attuatore.statoAnalisiPin
        ) ?? null
      );
    } else {
      return null;
    }
  }, [attuatore.statoAnalisiPin, proprietaNexusPinMisurate]);

  const riduzioneMappedValue = useMemo(() => {
    const rawValue = riduzionePinProp?.lastValue?.value;
    if (rawValue === undefined || rawValue === null) {
      return null;
    } else {
      return attuatore.riduzioneMappatura?.[rawValue] ?? null;
    }
  }, [riduzionePinProp, attuatore.riduzioneMappatura]);

  return (
    <Card sx={{ minWidth: 320, maxWidth: 320, minHeight: 320, maxHeight: 320 }}>
      <Box p={3} display="flex" flexDirection="column" gap={1.5} height="100%">
        <Typography
          variant="body1"
          fontWeight="bold"
          color="text"
          textAlign="center"
          sx={{ mb: 1, textTransform: "uppercase" }}
        >
          {attuatore.nome}
        </Typography>

        {/* Riduzione */}
        {riduzionePinProp?.lastValue ? (
          <Box>
            <Typography
              variant="caption"
              color="secondary"
              sx={{ fontWeight: "bold", textTransform: "uppercase" }}
            >
              {t("nexusAttuatore.riduzione")}{" "}
              {attuatore.riduzionePin ? `(${attuatore.riduzionePin})` : ""}
            </Typography>

            {/* valore */}
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="h5" fontWeight="bold">
                {riduzioneMappedValue
                  ? `${riduzioneMappedValue} RPM`
                  : riduzionePinProp.lastValue.value}
              </Typography>
              {riduzioneMappedValue === null && (
                <Box display="flex" alignItems="center" color="warning">
                  <WarningIcon color="warning" sx={{ fontSize: 20 }} />
                  <Typography variant="caption" sx={{ ml: 0.5 }}>
                    {t("nexusAttuatore.nonMappato")}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        ) : null}

        {/* Stato analisi */}
        {statoAnalisiPinProp?.lastValue ? (
          <Box>
            <Typography
              variant="caption"
              color="secondary"
              sx={{ fontWeight: "bold", textTransform: "uppercase" }}
            >
              {t("nexusAttuatore.statoAnalisi")}{" "}
              {attuatore.statoAnalisiPin
                ? `(${attuatore.statoAnalisiPin})`
                : ""}
            </Typography>
            <Typography
              variant="h5"
              fontWeight="bold"
              sx={{
                color:
                  statoAnalisiPinProp.lastValue.value === 1
                    ? "success.main"
                    : "error.main",
              }}
            >
              {statoAnalisiPinProp.lastValue.value === 1
                ? t("nexusAttuatore.statoAnalisiAttiva")
                : t("nexusAttuatore.statoAnalisiDisattiva")}
            </Typography>
          </Box>
        ) : null}

        {/* Sensori Collegati */}
        {modelli && attuatore.sensori && attuatore.sensori.length > 0 && (
          <Box
            mt="auto"
            display="flex"
            flexDirection="column"
            sx={{ overflow: "hidden" }}
          >
            <Typography
              variant="caption"
              color="secondary"
              sx={{
                fontWeight: "bold",
                textTransform: "uppercase",
                mb: 0.5,
              }}
            >
              {t("nexusAttuatore.sensoriCollegati")}
            </Typography>
            <Box
              sx={{
                maxHeight: 80,
                overflowY: "auto",
                pr: 1,
                "&::-webkit-scrollbar": {
                  width: "4px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(0,0,0,0.1)",
                  borderRadius: "4px",
                },
              }}
            >
              {attuatore.sensori.map((sensoreItem, index) => {
                const sensore = sensoreItem.sensoreNodo;
                const modello = modelli.find(
                  (m) => m.id === sensore?.dispositivoModelloId
                );

                if (!modello || !sensore?.dispositivoMatricola) return null;

                const isAnalisiAttiva =
                  statoAnalisiPinProp?.lastValue?.value === 1;
                const inibisceEmail = !!sensoreItem.statoAnalisiInibisceEmail;

                return (
                  <Box key={sensore.id} display="flex" flexDirection="column" mb={1}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2" fontWeight="medium">
                        {modello.modello}
                      </Typography>
                      {inibisceEmail && (
                        <Tooltip
                          title={
                            isAnalisiAttiva
                              ? t("nexusAttuatore.tooltip.notificheSospese")
                              : ""
                          }
                        >
                          {isAnalisiAttiva ? (
                            <NotificationsOffIcon
                              color="error"
                              sx={{ fontSize: 16 }}
                            />
                          ) : (
                            <NotificationsIcon
                              sx={{ fontSize: 16, color: "gray" }}
                            />
                          )}
                        </Tooltip>
                      )}
                    </Box>
                    <Typography variant="caption" color="text">
                      {t("nexusAttuatore.matricola")}{" "}
                      <Link
                        component={RouterLink}
                        to={`/nodi/${sensore.id}`}
                        style={{
                          color: "inherit",
                          textDecoration: "underline !important",
                        }}
                        className="underline-text"
                      >
                        {sensore.dispositivoMatricola}
                      </Link>
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </Box>
    </Card>
  );
};

export default NexusAttuatoreCard;
